"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Assets = void 0;
const internal_1 = require("../../internal");
const types_1 = require("../../types");
const conversion_1 = require("../../utils/conversion");
const internal_2 = require("../../utils/internal");
/**
 * Handles all Asset related functionality
 */
class Assets {
    /**
     * @hidden
     */
    constructor(context) {
        this.context = context;
        this.reserveTicker = (0, internal_2.createProcedureMethod)({
            getProcedureAndArgs: args => [internal_1.reserveTicker, args],
        }, context);
        this.createAsset = (0, internal_2.createProcedureMethod)({
            getProcedureAndArgs: args => [internal_1.createAsset, Object.assign({ reservationRequired: false }, args)],
        }, context);
        this.createNftCollection = (0, internal_2.createProcedureMethod)({
            getProcedureAndArgs: args => [internal_1.createNftCollection, args],
        }, context);
        this.registerCustomAssetType = (0, internal_2.createProcedureMethod)({
            getProcedureAndArgs: args => [internal_1.registerCustomAssetType, args],
        }, context);
    }
    // eslint-disable-next-line require-jsdoc
    isTickerAvailable(args, callback) {
        return __awaiter(this, void 0, void 0, function* () {
            const { context } = this;
            const reservation = new internal_1.TickerReservation(args, context);
            if (callback) {
                context.assertSupportsSubscription();
                return reservation.details(({ status: reservationStatus }) => {
                    // eslint-disable-next-line n/no-callback-literal, @typescript-eslint/no-floating-promises -- callback errors should be handled by the caller
                    callback(reservationStatus === types_1.TickerReservationStatus.Free);
                });
            }
            const { status } = yield reservation.details();
            return status === types_1.TickerReservationStatus.Free;
        });
    }
    /**
     * Retrieve all the ticker reservations currently owned by an Identity. This doesn't include Assets that
     *   have already been launched
     *
     * @param args.owner - defaults to the signing Identity
     *
     * @note reservations with unreadable characters in their tickers will be left out
     */
    getTickerReservations(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const { context: { polymeshApi: { query: { asset }, }, }, context, } = this;
            const did = yield (0, internal_2.getDid)(args === null || args === void 0 ? void 0 : args.owner, context);
            const rawDid = (0, conversion_1.stringToIdentityId)(did, context);
            const entries = yield asset.tickersOwnedByUser.entries(rawDid);
            const rawTickers = entries.map(([key]) => key.args[1]);
            const rawAssetIds = yield asset.tickerAssetId.multi(rawTickers);
            return rawAssetIds.reduce((result, rawAssetId, index) => {
                if (rawAssetId.isNone) {
                    const ticker = (0, conversion_1.tickerToString)(rawTickers[index]);
                    if ((0, internal_2.isPrintableAscii)(ticker)) {
                        return [...result, new internal_1.TickerReservation({ ticker }, context)];
                    }
                }
                return result;
            }, []);
        });
    }
    /**
     * Retrieve a Ticker Reservation
     *
     * @param args.ticker - Asset ticker
     */
    getTickerReservation(args) {
        const { ticker } = args;
        const { context } = this;
        return new internal_1.TickerReservation({ ticker }, context);
    }
    // eslint-disable-next-line require-jsdoc
    getAsset(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const { context } = this;
            const { ticker, assetId } = args;
            let assetIdValue = assetId;
            if (ticker) {
                assetIdValue = yield (0, internal_2.getAssetIdForTicker)(ticker, context);
            }
            return (0, internal_2.asAsset)(assetIdValue, context);
        });
    }
    /**
     * Retrieve all of the Assets owned by an Identity
     *
     * @param args.owner - Identity representation or Identity ID as stored in the blockchain
     *
     * @note Assets with unreadable characters in their tickers will be left out
     */
    getAssets(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const { context: { polymeshApi: { query: { asset }, }, }, context, } = this;
            const did = yield (0, internal_2.getDid)(args === null || args === void 0 ? void 0 : args.owner, context);
            const rawDid = (0, conversion_1.stringToIdentityId)(did, context);
            const entries = yield asset.securityTokensOwnedByUser.entries(rawDid);
            const ownedAssets = [];
            const rawAssetIds = [];
            entries.forEach(([key]) => {
                const rawAssetId = key.args[1];
                rawAssetIds.push(rawAssetId);
                ownedAssets.push((0, conversion_1.assetIdToString)(rawAssetId));
            });
            const ownedDetails = yield asset.assets.multi(rawAssetIds);
            return (0, internal_2.assembleAssetQuery)(ownedDetails, ownedAssets, context);
        });
    }
    /**
     * @hidden
     */
    getIdFromAssetIdAndTicker(assetId, ticker) {
        return __awaiter(this, void 0, void 0, function* () {
            const { context } = this;
            let assetIdValue;
            if (ticker) {
                assetIdValue = yield (0, internal_2.getAssetIdForTicker)(ticker, context);
            }
            else {
                assetIdValue = (0, internal_2.asUuid)(assetId);
            }
            return assetIdValue;
        });
    }
    // eslint-disable-next-line require-jsdoc
    getFungibleAsset(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const { ticker, assetId } = args;
            const { context } = this;
            const assetIdValue = yield this.getIdFromAssetIdAndTicker(assetId, ticker);
            const asset = new internal_1.FungibleAsset({ assetId: assetIdValue }, context);
            const exists = yield asset.exists();
            if (!exists) {
                throw new internal_1.PolymeshError({
                    code: types_1.ErrorCode.DataUnavailable,
                    message: `There is no Asset with ${ticker ? 'ticker' : 'asset ID'} "${ticker !== null && ticker !== void 0 ? ticker : assetId}"`,
                });
            }
            return asset;
        });
    }
    // eslint-disable-next-line require-jsdoc
    getNftCollection(args) {
        return __awaiter(this, void 0, void 0, function* () {
            const { ticker, assetId } = args;
            const { context } = this;
            const assetIdValue = yield this.getIdFromAssetIdAndTicker(assetId, ticker);
            const collection = new internal_1.NftCollection({ assetId: assetIdValue }, context);
            const exists = yield collection.exists();
            if (!exists) {
                throw new internal_1.PolymeshError({
                    code: types_1.ErrorCode.DataUnavailable,
                    message: `There is no NftCollection with ${ticker ? 'ticker' : 'asset ID'} "${ticker !== null && ticker !== void 0 ? ticker : assetId}"`,
                });
            }
            return collection;
        });
    }
    /**
     * Retrieve all the Assets on chain
     *
     * @note supports pagination
     */
    get(paginationOpts) {
        return __awaiter(this, void 0, void 0, function* () {
            const { context: { polymeshApi: { query: { asset }, }, }, context, } = this;
            const { entries, lastKey: next } = yield (0, internal_2.requestPaginated)(asset.assetNames, {
                paginationOpts,
            });
            const assetIds = [];
            const rawAssetIds = [];
            entries.forEach(([{ args: [rawAssetId], },]) => {
                rawAssetIds.push(rawAssetId);
                assetIds.push((0, conversion_1.assetIdToString)(rawAssetId));
            });
            const details = yield asset.assets.multi(rawAssetIds);
            const data = (0, internal_2.assembleAssetQuery)(details, assetIds, context);
            return {
                data,
                next,
            };
        });
    }
    /**
     * Retrieve all the Asset Global Metadata on chain. This includes metadata id, name and specs
     */
    getGlobalMetadataKeys() {
        return __awaiter(this, void 0, void 0, function* () {
            const { context: { polymeshApi: { query: { asset: { assetMetadataGlobalKeyToName, assetMetadataGlobalSpecs }, }, }, }, } = this;
            const [keyToNameEntries, specsEntries] = yield Promise.all([
                assetMetadataGlobalKeyToName.entries(),
                assetMetadataGlobalSpecs.entries(),
            ]);
            const specsEntryMap = new Map(specsEntries.map(([{ args: [rawKeyId], }, rawSpecs,]) => [rawKeyId.toString(), rawSpecs]));
            return keyToNameEntries.map(([{ args: [rawId], }, rawName,]) => {
                const rawSpecs = specsEntryMap.get(rawId.toString());
                return {
                    id: (0, conversion_1.u64ToBigNumber)(rawId),
                    name: (0, conversion_1.bytesToString)(rawName.unwrap()),
                    specs: (0, conversion_1.meshMetadataSpecToMetadataSpec)(rawSpecs),
                };
            });
        });
    }
    /**
     * Gets the next custom Asset type Id
     */
    getNextCustomAssetTypeId() {
        return __awaiter(this, void 0, void 0, function* () {
            const { context: { polymeshApi: { query: { asset: { customTypeIdSequence }, }, }, }, } = this;
            const rawId = yield customTypeIdSequence();
            return (0, conversion_1.u32ToBigNumber)(rawId).plus(1);
        });
    }
}
exports.Assets = Assets;
//# sourceMappingURL=Assets.js.map